import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  TextField,
  Dialog as ConfirmDialog,
} from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import AddIcon from '@mui/icons-material/Add'
import API_URL from '../../config/api'
import './users-panel.scss'

export default function UsersPanel({ open, onClose, user, onLogout }) {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showAddUser, setShowAddUser] = useState(false)
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [addingUser, setAddingUser] = useState(false)

  useEffect(() => {
    if (open && user?.role === 'admin') {
      fetchUsers()
    }
  }, [open, user])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error('Error al obtener usuarios')
      }

      const data = await response.json()
      setUsers(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleAddUser = async (e) => {
    e.preventDefault()

    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      setError('Por favor completa todos los campos')
      return
    }

    if (newUserData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres')
      return
    }

    if (newUserData.password !== newUserData.confirmPassword) {
      setError('Las contraseñas no coinciden')
      return
    }

    setAddingUser(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/api/auth/users/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newUserData.name,
          email: newUserData.email,
          password: newUserData.password,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Error al crear usuario')
      }

      setNewUserData({ name: '', email: '', password: '', confirmPassword: '' })
      setShowAddUser(false)
      await fetchUsers()
    } catch (err) {
      setError(err.message)
    } finally {
      setAddingUser(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    onLogout()
    onClose()
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getRoleLabel = (role) => {
    return role === 'admin' ? 'Administrador' : 'Usuario'
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="users-panel">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            Usuarios del Sistema
            <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.5 }}>
              Bienvenido, {user?.name}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {user?.role === 'admin' && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setShowAddUser(true)}
                variant="contained"
                size="small"
                sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                Nuevo Usuario
              </Button>
            )}
            <Button
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              color="error"
              variant="outlined"
              size="small"
            >
              Cerrar Sesión
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Correo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Rol</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Registrado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length > 0 ? (
                    users.map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell>{u.id}</TableCell>
                        <TableCell>{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{getRoleLabel(u.role)}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '20px',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              backgroundColor: u.status === 'active' ? '#c8e6c9' : '#ffccbc',
                              color: u.status === 'active' ? '#2e7d32' : '#d84315',
                            }}
                          >
                            {u.status}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(u.createdAt)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No hay usuarios registrados
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <ConfirmDialog open={showAddUser} onClose={() => setShowAddUser(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crear Nuevo Usuario</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            fullWidth
            label="Nombre completo"
            value={newUserData.name}
            onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
            disabled={addingUser}
          />
          <TextField
            fullWidth
            label="Correo"
            type="email"
            value={newUserData.email}
            onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
            disabled={addingUser}
          />
          <TextField
            fullWidth
            label="Contraseña"
            type="password"
            value={newUserData.password}
            onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
            disabled={addingUser}
          />
          <TextField
            fullWidth
            label="Confirmar contraseña"
            type="password"
            value={newUserData.confirmPassword}
            onChange={(e) => setNewUserData({ ...newUserData, confirmPassword: e.target.value })}
            disabled={addingUser}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddUser(false)} disabled={addingUser}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={addingUser}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {addingUser ? <CircularProgress size={24} /> : 'Crear'}
          </Button>
        </DialogActions>
      </ConfirmDialog>
    </>
  )
}

